import { DateRangeCalendar } from '@mui/x-date-pickers-pro/DateRangeCalendar';
import moment from 'moment';
import React, { memo, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';

import config from '../../../config';
import PickersLocalizationProvider from '../PickersLocalizationProvider/PickersLocalizationProvider';

const MINIMUM_NIGHTS = config.bookingUnitType === 'line-item/night' ? 1 : 0;

const toRange = (value) => [
  value && value.startDate ? moment(value.startDate) : null,
  value && value.endDate ? moment(value.endDate) : null,
];

/**
 * RHF bridge around MUI's DateRangeCalendar, replacing the former
 * DayPickerRangeController. The form field (`name`, e.g. "dates") only ever
 * holds a complete range `{ startDate: Date, endDate: Date }` — partial
 * selections live in internal state, matching the behavior
 * BookingDateRangeFilter relies on.
 *
 * `controllerRef` receives `{ onReset(startDate, endDate) }` for the
 * clear/cancel handlers. onReset only changes the visible calendar; the parent
 * FilterForm/FilterPopup handlers own submitting the cleared value.
 *
 * @param {object} props
 * @param {string} props.name - The name of the form field. Required.
 * @param {import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form. Required.
 * @param {Function} [props.controllerRef] - Ref callback receiving `{ onReset }`.
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name' | 'control'>} [props.controllerOptions] - Options for the controller.
 */
const ControlledDateRangeControllerComponent = ({
  name,
  control,
  controllerRef,
  controllerOptions = {},
  ...rest
}) => {
  if (!name) {
    throw new Error('ControlledDateRangeController: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledDateRangeController: control is required');
  }

  const { field } = useController({
    name,
    control,
    ...controllerOptions,
  });

  const [internalValue, setInternalValue] = useState(() => toRange(field.value));
  // `rangePosition` is controlled rather than left to MUI: after we reject a
  // same-day pick, MUI has already flipped its own position back to 'start'
  // (it counted the pick as a completed range), which would make the next click
  // restart the range instead of finishing it.
  const [rangePosition, setRangePosition] = useState('start');

  const maxDate = useMemo(
    () => moment().add(config.dayCountAvailableForBooking - 1, 'days').endOf('day'),
    []
  );

  const handleChange = useCallback(
    (newValue) => {
      const [start, end] = newValue;
      const isTooShort =
        start && end && moment(end).diff(moment(start), 'days') < MINIMUM_NIGHTS;
      // A rejected range keeps the start date and drops back to awaiting the end.
      const [nextStart, nextEnd] = isTooShort ? [start, null] : newValue;

      setInternalValue([nextStart, nextEnd]);
      // MUI calls onRangePositionChange *before* onChange, so setting the
      // position here reliably overrides whatever it proposed.
      setRangePosition(nextStart && !nextEnd ? 'end' : 'start');

      if (nextStart && nextEnd) {
        field.onChange({ startDate: nextStart.toDate(), endDate: nextEnd.toDate() });
      }
    },
    [field]
  );

  const onReset = useCallback((startDate, endDate) => {
    setInternalValue(startDate && endDate ? [moment(startDate), moment(endDate)] : [null, null]);
    setRangePosition('start');
  }, []);

  useImperativeHandle(controllerRef, () => ({ onReset }), [onReset]);

  const formRange = useMemo(() => toRange(field.value), [field.value]);

  // Mirrors the former react-dates DateRangeController: the form value reflects
  // the URL params and is authoritative as soon as a full range has been picked
  // internally — so an external reset (FilterForm re-running `reset(initialValues)`
  // when the parent re-renders) also clears the visible selection instead of
  // leaving the calendar showing a selection the form has already discarded.
  // While the selection is still partial only the internal state is meaningful.
  const displayValue = internalValue[0] && internalValue[1] ? formRange : internalValue;

  return (
    <PickersLocalizationProvider>
      <DateRangeCalendar
        calendars={1}
        disablePast
        maxDate={maxDate}
        onChange={handleChange}
        onRangePositionChange={setRangePosition}
        rangePosition={rangePosition}
        value={displayValue}
        {...rest}
      />
    </PickersLocalizationProvider>
  );
};

ControlledDateRangeControllerComponent.displayName = 'ControlledDateRangeController';

const ControlledDateRangeController = memo(ControlledDateRangeControllerComponent);

export default ControlledDateRangeController;
